import config from '../../utils/config';
import axiosInstance from '../../utils/axiosInstance';

// Shape returned by firebase-api Plan 88 DeviceLogsController.
export interface DeviceLogEntry {
  id: string;
  sessionId: string;
  userId: string;
  deviceName: string | null;
  ts: string;
  seq: number;
  level: 'debug' | 'info' | 'warn' | 'error';
  source: string;
  msg: string;
  ctx: Record<string, unknown> | null;
  appState: string;
  appVersion: string;
  platform: string;
}

export interface DeviceLogSession {
  sessionId: string;
  userId: string;
  deviceName: string | null;
  startTs: string;
  endTs: string;
  maxLevel: 'debug' | 'info' | 'warn' | 'error';
  entryCount: number;
  appVersion: string;
  platform: string;
}

function deviceLogsBase(): string {
  return `${config.firebaseApiUrl}/fb/device-logs`;
}

export async function fetchSessionsForDevice(deviceName: string): Promise<DeviceLogSession[]> {
  const url = `${deviceLogsBase()}/sessions`;
  const res = await axiosInstance.get<{ sessions: DeviceLogSession[] }>(url, {
    params: { deviceName },
  });
  return res.data.sessions ?? [];
}

export async function fetchSessionsForUser(uid: string): Promise<DeviceLogSession[]> {
  const url = `${deviceLogsBase()}/sessions`;
  const res = await axiosInstance.get<{ sessions: DeviceLogSession[] }>(url, {
    params: { uid },
  });
  return res.data.sessions ?? [];
}

export async function fetchOrphanedSessions(opts?: {
  uid?: string;
  since?: string;
  limit?: number;
}): Promise<DeviceLogSession[]> {
  const url = `${deviceLogsBase()}/orphaned-sessions`;
  const params: Record<string, string | number> = {};
  if (opts?.uid) params.uid = opts.uid;
  if (opts?.since) params.since = opts.since;
  if (opts?.limit) params.limit = opts.limit;
  const res = await axiosInstance.get<{ sessions: DeviceLogSession[] }>(url, {
    params: Object.keys(params).length ? params : undefined,
  });
  return res.data.sessions ?? [];
}

export async function fetchSessionTimeline(sessionId: string): Promise<DeviceLogEntry[]> {
  const url = `${deviceLogsBase()}/sessions/${encodeURIComponent(sessionId)}`;
  const res = await axiosInstance.get<{ entries: DeviceLogEntry[] }>(url);
  return res.data.entries ?? [];
}

export async function deleteSession(sessionId: string): Promise<number> {
  const url = `${deviceLogsBase()}/sessions/${encodeURIComponent(sessionId)}`;
  const res = await axiosInstance.delete<{ deleted: number }>(url);
  return res.data.deleted ?? 0;
}

export async function deleteAllForDevice(deviceName: string, before?: string): Promise<number> {
  const url = `${deviceLogsBase()}/by-device/${encodeURIComponent(deviceName)}`;
  const res = await axiosInstance.delete<{ deleted: number }>(url, {
    params: before ? { before } : undefined,
  });
  return res.data.deleted ?? 0;
}
